import { GridList, GridListItem } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { TableQueryWrapper } from '@/components/TableQueryWrapper';
import Badge from '@/design_system/Badge';
import Box from '@/design_system/Box';
import { InputDateRangeValue } from '@/design_system/InputDate/InputDateRange';
import Pagination, { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconBuilding from '@/icons/Building.svg';
import IconStore from '@/icons/Store.svg';
import IconStoreRepair from '@/icons/StoreRepair.svg';
import { useGetArticleName } from '@/models/article';
import {
  getAmountWithQuantity,
  getIssuerName,
  getRecipientName,
  InvoiceLineFromGetMany,
  useInvoiceLineName,
  useInvoiceLines,
} from '@/models/invoiceLine';
import { formatDate } from '@/utils/date';
import { formatCurrency } from '@/utils/number';
import useViewPort from '@/utils/useViewport';

import { InvoiceLinePaymentStrategy } from './components/filters/InvoiceLinePaymentStrategySelect';
import { InvoiceLinesEmptyState } from './components/InvoiceLinesEmptyState';
import { InvoiceLinesFilter } from './components/InvoiceLinesFilter';

const INVOICE_LINES_PER_PAGE = 20;

export const InvoiceLines = ({
  page,
  setPage,
  search,
  debouncedSearch,
  debouncedSetSearch,
  invoiceLineOrigin,
  setInvoiceLineOrigin,
  invoiceLineDestination,
  setInvoiceLineDestination,
  invoiceLinePaymentStrategy,
  setInvoiceLinePaymentStrategy,
  dateRange,
  setDateRange,
}: {
  page: number;
  setPage: (page: number) => void;
  search: string;
  debouncedSearch: string;
  debouncedSetSearch: (value: string) => void;
  invoiceLineOrigin: string;
  setInvoiceLineOrigin: (origin: string) => void;
  invoiceLineDestination: string;
  setInvoiceLineDestination: (destination: string) => void;
  invoiceLinePaymentStrategy: InvoiceLinePaymentStrategy | '';
  setInvoiceLinePaymentStrategy: (
    invoiceLinePaymentStrategy: InvoiceLinePaymentStrategy | ''
  ) => void;
  dateRange: InputDateRangeValue;
  setDateRange: (dateRange: InputDateRangeValue) => void;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  const {
    data: { invoiceLines, meta } = { invoiceLines: [] },
    isLoading,
    isError,
  } = useInvoiceLines({
    limit: INVOICE_LINES_PER_PAGE,
    offset: (page - 1) * INVOICE_LINES_PER_PAGE,
    search: debouncedSearch || undefined,
    originCompany: invoiceLineOrigin || undefined,
    destinationCompany: invoiceLineDestination || undefined,
    paymentStrategy: invoiceLinePaymentStrategy || undefined,
    createdAtFrom: dateRange.start,
    createdAtTo: dateRange.end,
  });

  const columnWidths = {
    service: 'minmax(180px, auto)',
    article: 'minmax(180px, 1fr)',
    request: 'minmax(100px, 1fr)',
    issuer: 'minmax(180px, 1fr)',
    recipient: 'minmax(180px, 1fr)',
    date: 'minmax(140px, auto)',
    amount: 'minmax(100px, auto)',
  };

  return (
    <Stack gap="1rem" style={{ height: '100%' }}>
      <InvoiceLinesFilter
        setPage={setPage}
        search={search}
        debouncedSetSearch={debouncedSetSearch}
        invoiceLineOrigin={invoiceLineOrigin}
        setInvoiceLineOrigin={setInvoiceLineOrigin}
        invoiceLineDestination={invoiceLineDestination}
        setInvoiceLineDestination={setInvoiceLineDestination}
        invoiceLinePaymentStrategy={invoiceLinePaymentStrategy}
        setInvoiceLinePaymentStrategy={setInvoiceLinePaymentStrategy}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />

      <TableQueryWrapper isLoading={isLoading} isError={isError}>
        {!invoiceLines.length ? (
          <InvoiceLinesEmptyState />
        ) : (
          <>
            {!isMobile && (
              <Table
                ariaLabel={t({
                  id: 'accounting.invoice-lines.table.label',
                  message: 'Services to associate list',
                })}
                columnWidths={[
                  columnWidths.service,
                  columnWidths.article,
                  columnWidths.request,
                  columnWidths.issuer,
                  columnWidths.recipient,
                  columnWidths.date,
                  columnWidths.amount,
                ]}
              >
                <Header>
                  <Row>
                    <Column>
                      <Trans id="accounting.invoice-lines.table.column.service">Service</Trans>
                    </Column>
                    <Column>
                      <Trans id="accounting.invoice-lines.table.column.article">Item</Trans>
                    </Column>
                    <Column>
                      <Trans id="accounting.invoice-lines.table.column.request">Request</Trans>
                    </Column>
                    <Column>
                      <Trans id="accounting.invoice-lines.table.column.issuer">Issuer</Trans>
                    </Column>
                    <Column>
                      <Trans id="accounting.invoice-lines.table.column.recipient">Recipient</Trans>
                    </Column>
                    <Column>
                      <Trans id="accounting.invoice-lines.table.column.date">Date</Trans>
                    </Column>
                    <Column>
                      <Trans id="accounting.invoice-lines.table.column.amount">Amount</Trans>
                    </Column>
                  </Row>
                </Header>

                <Body>
                  {invoiceLines.map((invoiceLine) => (
                    <InvoiceLineRow key={invoiceLine.id} invoiceLine={invoiceLine} />
                  ))}
                </Body>

                <PaginationFooter
                  page={page}
                  itemsPerPage={INVOICE_LINES_PER_PAGE}
                  count={meta?.count}
                  onPageChange={setPage}
                />
              </Table>
            )}

            {isMobile && (
              <>
                <GridList
                  aria-label={t({
                    id: 'accounting.invoice-lines.table.label',
                    message: 'Services to associate list',
                  })}
                  style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                >
                  {invoiceLines.map((invoiceLine) => (
                    <InvoiceLineCard key={invoiceLine.id} invoiceLine={invoiceLine} />
                  ))}
                </GridList>

                <Pagination
                  page={page}
                  itemsPerPage={INVOICE_LINES_PER_PAGE}
                  count={meta?.count}
                  onPageChange={setPage}
                />
              </>
            )}
          </>
        )}
      </TableQueryWrapper>
    </Stack>
  );
};

const InvoiceLineRow = ({ invoiceLine }: { invoiceLine: InvoiceLineFromGetMany }) => {
  const name = useInvoiceLineName(invoiceLine);
  const getArticleName = useGetArticleName();

  return (
    <Row>
      <Cell>
        <Stack gap="0.125rem" alignItems="flex-start">
          <Paragraph>{name}</Paragraph>
          {invoiceLine.paymentStrategy === 'store-cash-out' && (
            <Badge size="small" color="primary" hasBorder>
              <IconStore />
              <Trans id="accounting.invoice-lines.table.payment-strategy.store-cash-out">
                Store payment (cash out)
              </Trans>
            </Badge>
          )}
        </Stack>
      </Cell>
      <Cell>{invoiceLine.article ? getArticleName({ article: invoiceLine.article }) : '-'}</Cell>
      <Cell>{invoiceLine.request.reference}</Cell>
      <Cell>{getIssuerName(invoiceLine) || '-'}</Cell>
      <Cell>{getRecipientName(invoiceLine) || '-'}</Cell>
      <Cell>{formatDate(new Date(invoiceLine.createdAt), { dateStyle: 'medium' })}</Cell>
      <Cell justifyContent="flex-end">
        {formatCurrency(getAmountWithQuantity(invoiceLine.price), invoiceLine.price.currency)}
      </Cell>
    </Row>
  );
};

const InvoiceLineCard = ({ invoiceLine }: { invoiceLine: InvoiceLineFromGetMany }) => {
  const name = useInvoiceLineName(invoiceLine);
  const getArticleName = useGetArticleName();

  return (
    <GridListItem>
      <Box padding="16px" role="row" gap="8px">
        <Stack row alignItems="center" justifyContent="space-between" flexWrap="nowrap" gap="8px">
          <Stack>
            <InlineText>{name}</InlineText>
            <InlineText size="200" color="secondary">
              {!!invoiceLine.article && <>{getArticleName({ article: invoiceLine.article })} • </>}
              {invoiceLine.request.reference}
            </InlineText>
          </Stack>
          <InlineText>
            {formatCurrency(invoiceLine.price.amount, invoiceLine.price.currency)}
          </InlineText>
        </Stack>
        <Stack row gap="0.5rem" alignItems="center">
          <Badge color="neutral" size="large" borderRadius="soft" hasBorder>
            <IconStoreRepair />
            {getIssuerName(invoiceLine) || '-'}
          </Badge>
          <Badge color="neutral" size="large" borderRadius="soft" hasBorder>
            <IconBuilding />
            {getRecipientName(invoiceLine) || '-'}
          </Badge>
          {invoiceLine.paymentStrategy === 'store-cash-out' && (
            <Badge color="primary" size="large" borderRadius="soft" hasBorder>
              <IconStore />
              <Trans id="accounting.invoice-lines.table.payment-strategy.store-cash-out">
                Store payment (cash out)
              </Trans>
            </Badge>
          )}
        </Stack>
      </Box>
    </GridListItem>
  );
};
